import React from 'react';
import { formatMessage } from 'devextreme/localization';

import { PathChecklistItem, PathChecklistStatus } from '../../logics/pathmodule/PathBeforeSaveValidation';

interface PathPublishChecklistProps {
    Items: PathChecklistItem[];
}

const STATUS: Record<PathChecklistStatus, { glyph: string; color: string; background: string }> = {
    ok: { glyph: '✓', color: '#3BA55C', background: 'rgba(59, 165, 92, 0.15)' },
    blocksPublish: { glyph: '!', color: '#E0A100', background: 'rgba(224, 161, 0, 0.15)' },
    blocksSave: { glyph: '✕', color: '#E5484D', background: 'rgba(229, 72, 77, 0.15)' },
};

function captionFor(status: PathChecklistStatus): string {
    if (status === 'blocksSave') {
        return formatMessage('PathChecklist_BlocksSave');
    }
    if (status === 'blocksPublish') {
        return formatMessage('PathChecklist_BlocksPublish');
    }
    return '';
}

/**
 * "Publish readiness" report shown inside the MessageForm popup when a Save / Save and publish is
 * blocked (see `performPathSave`). It renders the item list from `getPathPublishChecklist` — the same
 * source of truth as the pre-save gate — so every blocking issue (duplicate names, missing
 * aggregation, no key, ...) is listed at once instead of one message at a time.
 */
export default function PathPublishChecklist({ Items }: PathPublishChecklistProps) {
    if (Items.length === 0) {
        return null;
    }

    const allOk = Items.every((item) => item.status === 'ok');

    return (
        <div style={{ width: '100%', padding: 16, background: '#1A1A20', borderRadius: 16, border: '1px #3D3D46 solid', boxSizing: 'border-box' }}>
            <div style={{ color: '#E3E3E8', fontSize: 12, fontFamily: 'Manrope', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.24, marginBottom: 12 }}>
                {formatMessage('PathChecklist_Title')}
            </div>

            {allOk ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#3BA55C', fontSize: 13, fontFamily: 'Manrope', fontWeight: 500 }}>
                    <Badge status="ok" />
                    {formatMessage('PathChecklist_Ready')}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {Items.map((item) => {
                        const lines = (item.details && item.details.length > 0) ? item.details : null;
                        const fallback = lines ? null : captionFor(item.status);
                        return (
                            <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontFamily: 'Manrope' }}>
                                <Badge status={item.status} />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <span style={{ color: '#E3E3E8', fontSize: 13, fontWeight: 500 }}>{item.label}</span>
                                    {lines
                                        ? lines.map((line, i) => (
                                            <span key={i} style={{ color: '#9393A2', fontSize: 12 }}>&mdash; {line}</span>
                                        ))
                                        : fallback !== '' && (
                                            <span style={{ color: '#9393A2', fontSize: 12 }}>&mdash; {fallback}</span>
                                        )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function Badge({ status }: { status: PathChecklistStatus }) {
    const s = STATUS[status];
    return (
        <span
            style={{
                flex: '0 0 auto',
                width: 18,
                height: 18,
                borderRadius: 9,
                background: s.background,
                color: s.color,
                fontSize: 11,
                fontWeight: 700,
                lineHeight: '18px',
                textAlign: 'center',
                alignSelf: 'center',
            }}
        >
            {s.glyph}
        </span>
    );
}
